import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Reveal from "./Reveal";
import { Mail, Send, Loader2, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", email: "", company: "", phone: "", message: "" };

const Contact = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      setForm(initial);
      toast.success("Thank you! Your message has been received.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-20 sm:py-28 bg-[#FAFAFA]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14">
        <Reveal>
          <div>
            <p className="text-xs uppercase tracking-widest text-[#D91C29] font-semibold">Get in touch</p>
            <h2 className="font-serif-display text-3xl sm:text-4xl font-bold text-[#0B1B3D] tracking-tight mt-3 leading-tight">
              Let&apos;s build something worth remembering
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed max-w-md">
              Whether you&apos;re planning a summit, launching a campaign or shaping a
              national conversation, our team is ready to help you make an impact.
            </p>

            <a
              href="mailto:info@urzumedia.com"
              data-testid="contact-email-link"
              className="mt-8 inline-flex items-center gap-4 rounded-2xl border border-slate-200 bg-white px-6 py-5 hover:border-[#D91C29] transition-colors duration-300"
            >
              <span className="w-12 h-12 rounded-xl bg-[#0B1B3D] flex items-center justify-center">
                <Mail size={22} className="text-white" />
              </span>
              <span>
                <span className="block text-xs uppercase tracking-widest text-slate-500">Email us</span>
                <span className="block font-semibold text-[#0B1B3D] text-lg">info@urzumedia.com</span>
              </span>
            </a>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="rounded-2xl border border-slate-200 bg-white p-7 sm:p-9 shadow-sm">
            {sent ? (
              <div data-testid="contact-success" className="flex flex-col items-center text-center py-10">
                <CheckCircle2 size={56} className="text-[#D91C29]" />
                <h3 className="font-serif-display text-2xl font-bold text-[#0B1B3D] mt-5">Message received</h3>
                <p className="mt-3 text-slate-600 max-w-sm">
                  Thank you for reaching out to Urzu Media. Our team will get back to you shortly.
                </p>
                <button
                  data-testid="contact-send-another"
                  onClick={() => setSent(false)}
                  className="mt-7 rounded-full border border-slate-300 px-6 py-2.5 font-semibold text-[#0B1B3D] hover:border-[#D91C29] transition-colors"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="contact-form" className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Full name *" testid="contact-name">
                    <input
                      data-testid="contact-name-input"
                      value={form.name}
                      onChange={update("name")}
                      placeholder="Your name"
                      className={inputCls}
                    />
                  </Field>
                  <Field label="Email *" testid="contact-email">
                    <input
                      data-testid="contact-email-input"
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      placeholder="you@company.com"
                      className={inputCls}
                    />
                  </Field>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Company" testid="contact-company">
                    <input
                      data-testid="contact-company-input"
                      value={form.company}
                      onChange={update("company")}
                      placeholder="Organisation"
                      className={inputCls}
                    />
                  </Field>
                  <Field label="Phone" testid="contact-phone">
                    <input
                      data-testid="contact-phone-input"
                      value={form.phone}
                      onChange={update("phone")}
                      placeholder="Optional"
                      className={inputCls}
                    />
                  </Field>
                </div>
                <Field label="Message *" testid="contact-message">
                  <textarea
                    data-testid="contact-message-input"
                    value={form.message}
                    onChange={update("message")}
                    rows={5}
                    placeholder="Tell us about your project or enquiry..."
                    className={`${inputCls} resize-none`}
                  />
                </Field>

                <button
                  type="submit"
                  data-testid="contact-submit-button"
                  disabled={loading}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#D91C29] text-white font-semibold px-7 py-3.5 shadow-sm hover:bg-[#b3141f] transition-colors duration-200 disabled:opacity-60"
                >
                  {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                  {loading ? "Sending..." : "Send message"}
                </button>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const inputCls =
  "w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#D91C29] focus:border-transparent transition-shadow";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-sm font-medium text-slate-700 mb-1.5">{label}</span>
    {children}
  </label>
);

export default Contact;
